import React, { useState, useEffect } from "react";

import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Outlet } from "react-router";

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import { DRAWER_WIDTH } from "./constants";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // Automatically adjust sidebar based on screen size
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      if (isSmall) {
        setSidebarVisible(false);
      }
    } else {
      setSidebarVisible(true);
      setSidebarOpen(true); // Always open on larger screens
    }
  }, [isMobile, isSmall]);

  const toggleSidebar = () => {
    if (!sidebarVisible) {
      setSidebarVisible(true);
      // Small delay to ensure visibility before animation
      setTimeout(() => setSidebarOpen(true), 50);
    } else if (sidebarOpen) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  };

  const hideSidebar = () => {
    setSidebarOpen(false);
    // Wait for animation to complete before hiding
    setTimeout(() => setSidebarVisible(false), 300);
  };

  // Common transition settings
  const layoutTransition = {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.default",
      }}
    >
      {/* Skip link for keyboard navigation */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: -9999,
          top: 0,
          zIndex: 60,
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          color: "primary.main",
          borderRadius: 1,
          fontWeight: 600,
          textDecoration: "none",
          "&:focus": {
            left: 16,
            top: 16,
          },
        }}
      >
        Skip to content
      </Box>

      {/* Topbar — full width, sticky */}
      <Topbar
        onExpandSidebar={() => {
          if (!sidebarOpen && sidebarVisible) {
            setSidebarOpen(true);
          }
        }}
      />

      {/* Body: sidebar + main content side by side */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Sidebar component */}
        <Sidebar
          open={sidebarOpen}
          visible={sidebarVisible}
          width={DRAWER_WIDTH}
          isMobile={isMobile}
          onClose={hideSidebar}
          onToggle={toggleSidebar}
        />

        {/* Main content wrapper */}
        <Box
          component="div"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            transition: theme.transitions.create(
              ["width", "margin"],
              layoutTransition,
            ),
          }}
        >
          {/* Main content */}
          <Box
            component="main"
            id="main-content"
            sx={{
              flexGrow: 1,
              padding: "24px",
              position: "relative",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
